/**
 * Date-versioned prices and capacities (SIB-939 / SIB-941).
 *
 * The backend records every price and capacity as an insert-only version with a `validFrom`
 * calendar date. The rules the frontend has to mirror live in the backend's `ValidFromPolicy`:
 *
 * - "today" is a German business day (`Europe/Berlin`), never the browser's UTC offset;
 * - a change to an existing value must be dated in the future;
 * - every version starts on the first day of a month, so the earliest date a change can take
 *   effect is the first day of next month;
 * - a version whose date has arrived can no longer be edited or dropped, only superseded.
 *
 * Everything here works on ISO calendar dates (`YYYY-MM-DD`) as plain strings, so a date never
 * passes through `Date`/UTC conversion that could shift it by a day.
 */

export const BUSINESS_TIME_ZONE = 'Europe/Berlin'

/**
 * Versions start on the first of a month (backend `ValidFromPolicy.assertFirstDayOfMonth`). Turn
 * this off if the backend ever accepts arbitrary future days; the picker and validation follow it.
 */
export const EFFECTIVE_DATE_FIRST_OF_MONTH_ONLY = true

/** How many months ahead the effective-date picker offers. */
export const EFFECTIVE_DATE_MONTHS_AHEAD = 24

export type IsoDate = string

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string') return false
  const m = ISO_DATE.exec(value)
  if (!m) return false
  const [, y, mo, d] = m
  const month = Number(mo)
  const day = Number(d)
  if (month < 1 || month > 12 || day < 1) return false
  return day <= daysInMonth(Number(y), month)
}

function daysInMonth(year: number, month: number) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

export function toIsoDate(year: number, month: number, day: number): IsoDate {
  return `${year}-${pad2(month)}-${pad2(day)}`
}

/** Today as an ISO calendar date in the business time zone, independent of the browser's zone. */
export function todayInBusinessZone(now: Date = new Date()): IsoDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** ISO dates sort lexically, so a plain string comparison is a correct date comparison. */
export function compareIsoDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const [y, m, d] = iso.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return toIsoDate(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate())
}

export function tomorrow(todayIso: IsoDate): IsoDate {
  return addDays(todayIso, 1)
}

export function firstOfMonth(iso: IsoDate): IsoDate {
  return `${iso.slice(0, 7)}-01`
}

export function addMonths(firstOfMonthIso: IsoDate, months: number): IsoDate {
  const [y, m] = firstOfMonthIso.split('-').map(Number)
  const total = y * 12 + (m - 1) + months
  return toIsoDate(Math.floor(total / 12), (total % 12) + 1, 1)
}

/**
 * The earliest date a change to an existing value may take effect. With the first-of-month rule
 * this is the first of next month; it coincides with "tomorrow" on the last day of a month.
 */
export function earliestEffectiveDate(todayIso: IsoDate): IsoDate {
  if (!EFFECTIVE_DATE_FIRST_OF_MONTH_ONLY) return tomorrow(todayIso)
  return addMonths(firstOfMonth(todayIso), 1)
}

/** The dates the effective-date picker offers, earliest first. Never includes today or the past. */
export function effectiveDateOptions(
  todayIso: IsoDate,
  count = EFFECTIVE_DATE_MONTHS_AHEAD
): IsoDate[] {
  const first = earliestEffectiveDate(todayIso)
  const out: IsoDate[] = []
  for (let i = 0; i < count; i++) {
    out.push(EFFECTIVE_DATE_FIRST_OF_MONTH_ONLY ? addMonths(first, i) : addDays(first, i))
  }
  return out
}

export type EffectiveDateProblem =
  | 'EMPTY'
  | 'INVALID'
  | 'NOT_FUTURE'
  | 'NOT_FIRST_OF_MONTH'
  | 'AFTER_HORIZON'

/**
 * Validates a typed or selected effective date against the rules a SIB is bound to. Today and the
 * past are rejected; so is any day other than the first of a month while that rule is active, and
 * any date after `latest` - the last date the planning horizon is open for (SIB-928), when known.
 */
export function validateEffectiveDate(
  value: string | null | undefined,
  todayIso: IsoDate,
  latest?: IsoDate | null
): EffectiveDateProblem | null {
  const v = (value ?? '').trim()
  if (!v) return 'EMPTY'
  if (!isIsoDate(v)) return 'INVALID'
  if (compareIsoDates(v, todayIso) <= 0) return 'NOT_FUTURE'
  if (EFFECTIVE_DATE_FIRST_OF_MONTH_ONLY && !v.endsWith('-01')) return 'NOT_FIRST_OF_MONTH'
  if (latest && compareIsoDates(v, latest) > 0) return 'AFTER_HORIZON'
  return null
}

export function effectiveDateProblemMessage(
  problem: EffectiveDateProblem,
  todayIso: IsoDate,
  latest?: IsoDate | null
): string {
  const earliest = formatIsoDateDE(earliestEffectiveDate(todayIso))
  switch (problem) {
    case 'EMPTY':
      return 'Bitte wählen Sie, ab wann die Änderung gilt.'
    case 'INVALID':
      return 'Ungültiges Datum.'
    case 'NOT_FUTURE':
      return `Das Datum muss in der Zukunft liegen. Frühestens möglich: ${earliest}.`
    case 'NOT_FIRST_OF_MONTH':
      return `Änderungen gelten immer ab dem 1. eines Monats. Frühestens möglich: ${earliest}.`
    case 'AFTER_HORIZON':
      return latest
        ? `Für dieses Datum ist die Planung noch nicht geöffnet. Spätestens möglich: ${formatIsoDateDE(latest)}.`
        : 'Für dieses Datum ist die Planung noch nicht geöffnet.'
  }
}

/** `2027-01-01` → `01.01.2027`, without going through `Date`. */
export function formatIsoDateDE(iso: IsoDate | null | undefined): string {
  if (!iso || !isIsoDate(iso)) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

/**
 * The `validFrom` the SIB-939 backfill gave every value that existed before versioning. It means
 * "has always been this", not a real date, so it is never shown as one.
 */
export const BASELINE_VALID_FROM: IsoDate = '1970-01-01'

export function isBaselineDate(iso: IsoDate | null | undefined): boolean {
  return iso === BASELINE_VALID_FROM
}

/** A `validFrom` for display: the baseline reads "von Anfang an", everything else as a date. */
export function formatEffectiveFromDE(iso: IsoDate | null | undefined): string {
  if (isBaselineDate(iso)) return 'von Anfang an'
  return formatIsoDateDE(iso)
}

export const MONTH_LABELS_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const

export const MONTH_LABELS_DE_SHORT = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
] as const

/** `2027-01-01` → `Januar 2027`. */
export function formatMonthYearDE(iso: IsoDate | null | undefined): string {
  if (!iso || !isIsoDate(iso)) return '—'
  const [y, m] = iso.split('-')
  return `${MONTH_LABELS_DE[Number(m) - 1]} ${y}`
}

/** A backend `LocalDateTime` (`2026-09-22T10:15:30`) → `22.09.2026, 10:15`. */
export function formatDateTimeDE(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** `115` → `115,00 EUR`. Zero is a value, not a blank. */
export function formatEuro(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return `${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`
}

/** `12` → `12`, `0` → `0`, never a blank for zero. */
export function formatCapacity(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value.toLocaleString('de-DE')
}

/**
 * Parses a monetary input. Accepts `100`, `100.5`, `100.50` and the German `100,50`; rejects more
 * than two decimals so the stored precision matches what the invoice prints.
 */
export function parsePriceInput(raw: string | null | undefined): number | null {
  const v = (raw ?? '').trim().replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null
  return Number(v)
}

/** Parses a capacity input: a non-negative whole number. `0` is valid. */
export function parseCapacityInput(raw: string | null | undefined): number | null {
  const v = (raw ?? '').trim()
  if (!/^\d+$/.test(v)) return null
  return Number(v)
}

/**
 * Who recorded a version. The backend returns the actor's user id; the SIB is shown by name, any
 * other actor can only be a Polygran administrator acting on the SIB's behalf.
 */
export function actorLabel(
  actorId: string | null | undefined,
  currentUser: { id: string; displayName?: string | null } | null | undefined
): string {
  if (!actorId) return '—'
  if (currentUser && actorId === currentUser.id) {
    return currentUser.displayName?.trim() || 'Sie'
  }
  return 'Administrator'
}

export function isAdminActor(
  actorId: string | null | undefined,
  currentUser: { id: string } | null | undefined
): boolean {
  return !!actorId && !!currentUser && actorId !== currentUser.id
}

/* ------------------------------------------------------------------------------------------- */
/* Pending changes and confirmation                                                             */
/* ------------------------------------------------------------------------------------------- */

export type ValueKind = 'price' | 'capacity'

/** A planned version as embedded in a settings row. */
export type PlannedValue = {
  id: number
  value: number
  validFrom: IsoDate
  createdBy: string
  createdAt: string
}

/** One service's change as the user is about to save it. */
export type PendingValueChange = {
  serviceCode: string
  serviceLabel: string
  /** The value in effect today, null when this service has no value yet. */
  activeValue: number | null
  newValue: number
  /** null only for the very first value, which takes effect on the first of the current month. */
  validFrom: IsoDate | null
  /** The planned change this save replaces, when the user edited a planned change. */
  replaces: PlannedValue | null
}

export function formatValue(kind: ValueKind, value: number | null | undefined): string {
  return kind === 'price' ? formatEuro(value) : formatCapacity(value)
}

export function kindLabel(kind: ValueKind): string {
  return kind === 'price' ? 'Preis' : 'Kapazität'
}

/**
 * The plain-text line the confirmation shows, e.g.
 * `Preis TOJ: 100,00 EUR → 115,00 EUR / Gültig ab: 01.01.2027`.
 *
 * The old value is what the change supersedes: the planned value when a planned change is being
 * edited, otherwise the value in effect today.
 */
export function confirmationLine(kind: ValueKind, change: PendingValueChange): string {
  const supersedes = change.replaces ? change.replaces.value : change.activeValue
  const from = change.validFrom ? formatIsoDateDE(change.validFrom) : 'sofort'
  return `${kindLabel(kind)} ${change.serviceLabel}: ${formatValue(kind, supersedes)} → ${formatValue(kind, change.newValue)} / Gültig ab: ${from}`
}

/* ------------------------------------------------------------------------------------------- */
/* History                                                                                      */
/* ------------------------------------------------------------------------------------------- */

export type VersionRow = {
  id: number
  serviceCode: string
  serviceLabel: string
  value: number
  validFrom: IsoDate
  state: 'PAST' | 'CURRENT' | 'FUTURE' | 'DROPPED'
  createdBy: string
  createdAt: string
  discardedBy: string | null
  discardedAt: string | null
}

export type HistoryRow = VersionRow & {
  /** The value this version superseded, or would have superseded; null for the first version. */
  oldValue: number | null
  dropped: boolean
}

/**
 * Turns the version rows into history rows: newest first, each with the value it superseded.
 *
 * The backend returns no old value - it is derived here from the non-dropped version of the same
 * service that was in effect the day before this one took effect. A dropped change is kept and
 * flagged; it never counts as anybody's old value.
 */
export function buildHistoryRows(rows: VersionRow[]): HistoryRow[] {
  const byService = new Map<string, VersionRow[]>()
  for (const row of rows) {
    if (row.state === 'DROPPED') continue
    const list = byService.get(row.serviceCode) ?? []
    list.push(row)
    byService.set(row.serviceCode, list)
  }
  for (const list of byService.values()) {
    list.sort((a, b) => compareIsoDates(a.validFrom, b.validFrom) || a.id - b.id)
  }

  const oldValueOf = (row: VersionRow): number | null => {
    const list = byService.get(row.serviceCode) ?? []
    let previous: VersionRow | null = null
    for (const candidate of list) {
      if (candidate.id === row.id) continue
      if (compareIsoDates(candidate.validFrom, row.validFrom) < 0) previous = candidate
      else break
    }
    return previous ? previous.value : null
  }

  return [...rows]
    .map(row => ({ ...row, oldValue: oldValueOf(row), dropped: row.state === 'DROPPED' }))
    .sort((a, b) => {
      const byCreated = (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
      return byCreated !== 0 ? byCreated : b.id - a.id
    })
}

/* ------------------------------------------------------------------------------------------- */
/* Backend error interpretation                                                                 */
/* ------------------------------------------------------------------------------------------- */

/**
 * Whether a failed write or drop means the version's date arrived while the screen was open. The
 * backend answers 409 for that on some paths and 400 with a descriptive message on others; both
 * call for the same reaction: reload and explain that the change has already taken effect.
 */
export function isAlreadyInEffectError(err: unknown): boolean {
  const e = err as { status?: number; message?: string; data?: { message?: string } } | null
  if (!e) return false
  if (e.status === 409) return true
  const msg = `${e.message ?? ''} ${e.data?.message ?? ''}`.toLowerCase()
  return (
    msg.includes('already in effect') ||
    msg.includes('can no longer be dropped') ||
    msg.includes('can no longer be edited')
  )
}

/** The backend's own explanation of a rejected date, if it sent one. */
export function backendMessageOf(err: unknown): string | null {
  const e = err as { message?: string; data?: { message?: string } } | null
  const msg = (e?.data?.message || e?.message || '').trim()
  return msg && msg !== 'Validation failed' ? msg : null
}
